import {Autocomplete, InputAdornment, TextField} from "@mui/material";
import React, {CSSProperties} from "react";
import {Component} from "react";
import {NavigateFunction} from "react-router-dom";
import {Question} from "../../interfaces";
import {connect} from "react-redux";
import {withHtmlParser, withRouter} from "../../utilities/hooks";
import Icon from "../Icon";
import {scanKnowledgeBase} from "../../utilities/scanKB";
import {KBSettings} from "src/interfaces/settings";

interface Option {
  question: string;
  answer: string;
  id: number;
}

interface SearchProps {
  language: string;
  navigate: NavigateFunction;
  placeholder: string;
  parser: any;
  getInnerText: any;
  questionClickHandler?: Function;
  style?: CSSProperties;
  questions: Question[];
  settings: KBSettings;
}

interface SearchState {
  searchText: string;
  foundQuestions: Question[];
  isFocused: boolean;
}

class Search extends Component<SearchProps, SearchState> {
  constructor(props) {
    super(props);
    this.state = {
      searchText: "",
      foundQuestions: [],
      isFocused: false,
    };
  }

  inputRef = React.createRef();

  componentDidMount(): void {
    if (this.inputRef.current) {
      (this.inputRef.current as HTMLInputElement).focus();
    }
  }

  render() {
    const options: Option[] = this.state.foundQuestions.map((question) => {
      return {
        question: question?.langs[this.props.language]?.question || "",
        answer: question?.langs[this.props.language]?.answer || "",
        id: question.id,
      };
    });
    return (
      <div
        className="input-container"
        style={{justifyContent: "center"}}
        role="search"
        aria-label={this.props.placeholder}
      >
        <Autocomplete
          filterOptions={(x) => x}
          className="search-box"
          onBlur={this.clearSearch}
          clearOnEscape
          open={options.length > 0}
          loadingText={"Loading..."}
          size="small"
          sx={{
            width: "100%",
            borderRadius: "40px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "40px",
              fieldset: {
                transition: "border-color 0.3s ease",
                borderColor: `${this.props.settings?.widgetSettings?.buttonColor}4D` || "#003eff",
              },
              "&:hover fieldset": {
                borderColor: `${this.props.settings?.widgetSettings?.buttonColor}` || "#003eff",
              },
              "&.Mui-focused fieldset": {
                borderColor: `${this.props.settings?.widgetSettings?.buttonColor}` || "#003eff",
                borderWidth: "1px",
              },
            },
          }}
          id="search-box"
          onInputChange={(e) => {
            this.handleSearchInputChange(e);
          }}
          popupIcon={null}
          options={options}
          getOptionLabel={(option: Option) => option.question}
          onChange={(e, nv) => nv && this.handleQuestionClick((nv as any).id)}
          renderOption={(props, option) => {
            return (
              <li {...props} key={option.id} role="option" aria-label={option.question}>
                {option.question}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              className="faq-widget-search-input"
              inputRef={this.inputRef}
              label={!this.state.isFocused ? this.props.placeholder : ""}
              onKeyDown={(e) => {
                if (e.key === "Enter" && options.length > 0) {
                  this.handleQuestionClick(options[0].id);
                } else if (e.key === "Escape") {
                  this.clearSearch();
                }
              }}
              InputProps={{
                ...params.InputProps,
                onFocus: () => this.setState({isFocused: true}),
                onBlur: () => this.setState({isFocused: false}),
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon name="search" iconSize="16px" style={{paddingLeft: "10px"}} aria-hidden="true" />
                  </InputAdornment>
                ),
                endAdornment: <React.Fragment>{params.InputProps.endAdornment}</React.Fragment>,
                inputProps: {
                  ...params.inputProps,
                  style: {paddingLeft: "0px"},
                  "aria-label": this.props.placeholder,
                  "aria-expanded": options.length > 0,
                  "aria-controls": "search-listbox",
                  "aria-autocomplete": "list",
                  role: "combobox",
                },
              }}
              InputLabelProps={{
                ...params.InputLabelProps,
                shrink: params.inputProps.value ? true : false,
                style: !this.state.isFocused ? {paddingLeft: "25px"} : {},
              }}
            />
          )}
          ListboxProps={{
            id: "search-listbox",
            role: "listbox",
            "aria-label": "Search results",
          }}
        />
      </div>
    );
  }

  handleSearchInputChange(e) {
    const inputValue = e.target.value.toString();

    if (inputValue.length > 0) this.searchQuestions(inputValue);
    else if (inputValue.length === 0) this.clearSearch();
  }

  clearSearch = () => {
    this.setState({foundQuestions: [], searchText: ""});
  };

  handleQuestionClick(questionId: number) {
    const question = this.state.foundQuestions.find((q) => q.id === questionId);

    if (this.props.questionClickHandler) this.props.questionClickHandler(question);
    else this.props.navigate(`/question/${questionId}`);
  }

  searchQuestions(searchText: string) {
    const foundQuestions = scanKnowledgeBase(searchText, this.props.questions, null, this.props.language).questions;
    const filteredQuestions = foundQuestions.filter(
      (q) => !q.langs[this.props.language]?.hidden && !q.langs[this.props.language]?.internal && !q.deleted,
    );

    this.setState({foundQuestions: filteredQuestions, searchText});
  }
}

const mapStateToProps = (state: {questions: Question[]; settings: {settings: KBSettings}}) => {
  return {
    questions: state.questions,
    settings: state.settings.settings,
  };
};

export default withHtmlParser(connect(mapStateToProps)(withRouter(Search)));
